import { lazy } from 'react';
import { staffRoutes } from './staff';
import { Outlet } from 'react-router-dom';
import type { RouteConfig } from './types';
import { authRoutes } from './auth.routes';
import { userRoutes } from './user.routes';
import { adminRoutes } from './admin/admin.routes';
import { ComponentSuspense } from '@/components/ui/suspense-wrapper';

const DashboardLayout = lazy(() => import('@/components/layout/dashboard-layout'));
const MyDelegationsPage = lazy(() => import('@/pages/app/delegation/my-delegations'));
const MyDelegationFormPage = lazy(() => import('@/pages/app/delegation/my-delegation-form'));
const ToDoPage = lazy(() => import('@/pages/app/to-do/to-do'));
const StartTriggeredApplicationPage = lazy(() => import('@/pages/app/to-do/start-triggered-application'));
const NotFoundPage = lazy(() => import('@/pages/not-found'));
const CertificateVerificationPage = lazy(() => import('@/pages/public/certificate-verify/certificate-verification'));
const DelegationFormPage = lazy(() => import('@/pages/public/delegation/delegation-form'));

const authenticatedLayoutRoute: RouteConfig = {
  path: "/",
  element: (
    <ComponentSuspense >
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    </ComponentSuspense>
  ),
  meta: {
    requiresAuth: true
  },
  children: [
    ...userRoutes.map(route => ({
      ...route,
      meta: {
        ...route.meta,
        roles: ["User"],
        requiresAuth: true
      }
    })),
    ...adminRoutes.map(route => ({
      ...route,
      meta: {
        ...route.meta,
        roles: ["Admin"],
        requiresAuth: true
      }
    })),
    ...staffRoutes.map(route => ({
      ...route,
      meta: {
        ...route.meta,
        roles: ["Staff"],
        requiresAuth: true
      }
    })),
    // Any signed-in recipient, whatever their role — so no `roles` restriction.
    {
      path: "delegated-to-me",
      element: (
        <ComponentSuspense>
          <MyDelegationsPage />
        </ComponentSuspense>
      ),
      meta: { requiresAuth: true, title: "Delegated to me" }
    },
    {
      path: "delegated-to-me/:id",
      element: (
        <ComponentSuspense>
          <MyDelegationFormPage />
        </ComponentSuspense>
      ),
      meta: { requiresAuth: true, title: "Delegated form" }
    },
    // Pending starts from process triggers can target staff or admins too,
    // so like delegations these carry no `roles` restriction.
    {
      path: "to-do",
      element: (
        <ComponentSuspense>
          <ToDoPage />
        </ComponentSuspense>
      ),
      meta: { requiresAuth: true, title: "To do" }
    },
    {
      path: "to-do/:runId",
      element: (
        <ComponentSuspense>
          <StartTriggeredApplicationPage />
        </ComponentSuspense>
      ),
      meta: { requiresAuth: true, title: "Start application" }
    }
  ]
};

const notFoundRoute: RouteConfig = {
  path: "*",
  element: (
    <ComponentSuspense >
      <NotFoundPage />
    </ComponentSuspense>
  ),
  meta: {
    requiresAuth: false,
    title: "UzaForm | Not Found",
    description: "Page not found "
  }
};

const publicCertificateRoute: RouteConfig = {
  path: "/certificate/verify/:verificationCode?",
  element: (
    <ComponentSuspense>
      <CertificateVerificationPage />
    </ComponentSuspense>
  ),
  meta: {
    requiresAuth: false,
    title: "Certificate Verification",
    description: "Verify certificate authenticity"
  }
};

const publicDelegationRoute: RouteConfig = {
  path: "/delegation/:token",
  element: (
    <ComponentSuspense>
      <DelegationFormPage />
    </ComponentSuspense>
  ),
  meta: {
    requiresAuth: false,
    title: "Shared Form",
    description: "Fill in a form that was shared with you"
  }
};

export const allRoutes = [
  ...authRoutes.map(route => ({
    ...route,
    meta: {
      ...route.meta,
      requiresAuth: false
    }
  })),
  publicCertificateRoute,
  publicDelegationRoute,
  authenticatedLayoutRoute,
  notFoundRoute,
];